import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { parseApiError } from '@cast/api-client';
import { notify } from '@cast/design-system';
import { apiClient } from '@cast/infrastructure-api';
import { CreateSelectionTemplate, NodeSelectionTemplate } from '@cast/types';

import { getClustersNodeTemplatesQueryKey } from 'hooks/queries/autoscaler/useClustersNodeTemplatesQuery';
import { getSelectionTemplatesQueryKey } from 'hooks/queries/autoscaler/useSelectionTemplatesQuery';
import { getClusterEdgeLocationsQueryKey } from 'hooks/queries/omni/useGetClusterEdgeLocationsQuery';

type UseCreateSelectionTemplateMutationArgs = {
  id: string;
  onSuccess?: () => void;
};

export const useCreateSelectionTemplateMutation = ({
  id,
  onSuccess,
}: UseCreateSelectionTemplateMutationArgs): UseMutationResult<
  AxiosResponse<NodeSelectionTemplate>,
  unknown,
  CreateSelectionTemplate,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (template) => {
      return apiClient.autoscaler.createSelectionTemplate({
        clusterId: id,
        nodeTemplate: template,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getSelectionTemplatesQueryKey(id),
      });
      queryClient.invalidateQueries({
        queryKey: getClusterEdgeLocationsQueryKey({ clusterId: id }),
      });
      queryClient.invalidateQueries({
        queryKey: getClustersNodeTemplatesQueryKey(),
      });
      onSuccess?.();
    },
    onError: (error: unknown) => {
      notify.error(parseApiError(error));
    },
  });
};
